import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getInvoices, InvoiceSummary } from '@shared/ui/organisms/stripe/stripeService';
import { BodyLarge, BodyRegular, BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from 'react-i18next';
import Colours from '@shared/ui/atoms/Colours';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { TrackJS } from 'trackjs';

export function PastInvoicesSection() {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    getInvoices()
      .then((data: InvoiceSummary[]) => {
        if (mounted) {
          setInvoices(data ?? []);
          setLoaded(true);
        }
      })
      .catch((e: any) => {
        console.error(e);
        TrackJS?.track(e);
        if (mounted) {
          setError(true);
          setLoaded(true);
        }
      });
    return () => { mounted = false; };
  }, []);

  if (!loaded) {
    return (
      <Container>
        <BodyLarge weight={600} style={{ marginBottom: 12 }}>{t('payments.past-invoices')}</BodyLarge>
        <SpinnerWrapper>
          <LoadingSpinner hideBorder={true} hideText={true} />
        </SpinnerWrapper>
      </Container>
    );
  }

  const formatDate = (epochSeconds: number) =>
    epochSeconds ? new Date(epochSeconds * 1000).toLocaleDateString() : '';

  const formatAmount = (inv: InvoiceSummary) => {
    const value = (inv.amount ?? 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (inv.currency || 'gbp').toUpperCase(),
      }).format(value);
    } catch {
      return value.toFixed(2);
    }
  };

  return (
    <Container>
      <BodyLarge weight={600} style={{ marginBottom: 12 }}>{t('payments.past-invoices')}</BodyLarge>

      {error ?
        <BodyRegular>{t('payments.invoices-load-failed')}</BodyRegular> :
        invoices.length === 0 ?
          <BodyRegular>{t('payments.no-invoices')}</BodyRegular> :
          <>
            <HeaderRow>
              <HeaderCell>{t('payments.invoice-date')}</HeaderCell>
              <HeaderCell>{t('payments.invoice-amount')}</HeaderCell>
              <HeaderCell>{t('payments.invoice-status')}</HeaderCell>
              <HeaderCell />
            </HeaderRow>
            {invoices.map((inv, i) => (
              <InvoiceRow key={inv.number ?? `${inv.created}-${i}`}>
                <Cell>{formatDate(inv.created)}</Cell>
                <Cell>{formatAmount(inv)}</Cell>
                <StatusCell>{inv.status}</StatusCell>
                <LinkCell>
                  {inv.url &&
                    <a href={inv.url} target="_blank" rel="noreferrer">{t('payments.view-invoice')}</a>}
                </LinkCell>
              </InvoiceRow>
            ))}
          </>}
    </Container>
  );
}

const Container = styled.div`
  margin-bottom: 32px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${Colours.mildGrey};
`;

const HeaderCell = styled(BodyVerySmall)`
  flex: 1;
  margin: 0;
  color: ${Colours.darkGrey};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const InvoiceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${Colours.mildGrey};
`;

const Cell = styled(BodySmall)`
  flex: 1;
  margin: 0;
`;

const StatusCell = styled(BodySmall)`
  flex: 1;
  margin: 0;
  text-transform: capitalize;
`;

const LinkCell = styled(BodySmall)`
  flex: 1;
  margin: 0;
  text-align: right;

  a {
    color: ${Colours.blue};
  }
`;
